import { Box, Flex, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { LoadingAnimation } from 'src/UI/app-container/chat/components/LoadingAnimation';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useCalendars } from 'src/meetings/hooks/useCalendars';

type ContainerBounds = {
  left: number;
  width: number;
};

export const EventsLoadingBanner = () => {
  const { connecting } = useCalendars();
  const [isFixed, setIsFixed] = useState(false);
  const [containerBounds, setContainerBounds] = useState<ContainerBounds | null>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!connecting) {
      setIsFixed(false);
      return;
    }

    const updateContainerBounds = () => {
      if (bannerRef.current) {
        const rect = bannerRef.current.getBoundingClientRect();
        setContainerBounds({
          left: rect.left,
          width: rect.width,
        });
      }
    };
    updateContainerBounds();
    window.addEventListener('resize', updateContainerBounds);

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFixed(!entry.isIntersecting);
        if (!entry.isIntersecting) {
          updateContainerBounds();
        }
      },
      {
        root: null,
        threshold: 0,
        rootMargin: '0px 0px -36px 0px',
      },
    );

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => {
      if (bannerRef.current) {
        observer.unobserve(bannerRef.current);
      }
      window.removeEventListener('resize', updateContainerBounds);
    };
  }, [connecting]);

  if (!connecting) {
    return null;
  }

  return (
    <>
      <Box
        ref={bannerRef}
        mt="xxs"
        style={{
          visibility: isFixed ? 'hidden' : 'visible',
        }}
      >
        <BannerContent />
      </Box>

      {isFixed && containerBounds && (
        <Box
          style={{
            position: 'fixed',
            bottom: 36,
            left: containerBounds.left,
            width: containerBounds.width,
            zIndex: 1000,
          }}
        >
          <BannerContent />
        </Box>
      )}
    </>
  );
};

const BannerContent = () => (
  <Flex
    align="center"
    gap="xs"
    bg="brand.2"
    px="xl"
    py="xxs"
    style={{
      borderRadius: 9999,
    }}
  >
    <Box style={{ overflow: 'none' }} mt="-xxxs" w={46} h={20}>
      <LoadingAnimation color="brand" size={16} />
    </Box>
    <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10">
      Hang tight! We’re adding your meetings - you can keep exploring while it syncs.
    </Text>
  </Flex>
);
